import './index.css'

import { base64ToBlob, blobTobase64 } from '@disco/utils/convertImage'
import { pushUINotification } from '@disco/utils/notifications'

import React, { useState } from 'react'
import { Flex, Link } from 'components/ds'
import BannerImageRecommendationsHoverCard from 'components/common/banner-image-recommendations-hover-card'
import CroppingTool from 'components/ui/cropping-tool'
import FileSelector from 'components/ui/file-selector'
import Heading from 'components/ui/heading'
import styles from './index.module.css'

const getProcessedLogo = logo => {
  const encodedLogo = encodeURIComponent(logo)

  if (process.env.NEXT_PUBLIC_IS_CUSTOM_DOMAIN === 'true') {
    return '/api/backfill?v=1&w=320&h=320&u=' + encodedLogo
  }

  return window.host + '/cat/api/backfill?v=1&w=320&h=320&u=' + encodedLogo
}

const getBackgroundStyle = (imageUrl: string) =>
  imageUrl
    ? {
        backgroundImage: `url("${imageUrl}")`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }
    : {}

type Props = {
  title: React.ReactNode
  description: React.ReactNode | null | undefined
  images: Record<string, any>
  bannerImageNode?: React.ReactNode
  onImagesChange?: (...args: Array<any>) => any
}

// constant should match value in LibraryHero.tsx
const BANNER_HEIGHT = 300

// Must fit within the cropping tool's --modal-width (550px) to avoid transparent
// pixel edges caused by the canvas being larger than the rendered image area.
const BANNER_CROP_WIDTH = 549
const BANNER_CROP_HEIGHT = 183

/**
 * Validates that a base64 string can be loaded as an image.
 * Returns true if the image loads successfully, false otherwise.
 *
 * This validation is necessary because blobTobase64 only converts the file bytes
 * to a base64 string - it doesn't verify that the data represents a valid,
 * renderable image. A file could have an image extension but contain corrupt
 * or non-image data. This function attempts to actually load the image in the
 * browser, catching cases that would otherwise fail silently in the cropping tool.
 */
const validateBase64Image = (base64Image: string): Promise<boolean> => {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      img.onload = null
      img.onerror = null
      img.src = ''
      resolve(true)
    }
    img.onerror = () => {
      img.onload = null
      img.onerror = null
      img.src = ''
      resolve(false)
    }
    img.src = base64Image
  })
}

/**
 * Library header component with banner and logo upload functionality
 * Displays library title, description, and handles image management
 * @param title - Library title to display
 * @param description - Optional library description
 * @param images - Object containing banner and logo image URLs
 * @param bannerImageNode - Optional custom banner content
 * @param onImagesChange - Callback when images are changed
 */
const LibraryHeader = (props: Props): React.ReactElement<'div'> => {
  const [croppingToolOptions, setCroppingToolOptions] = useState<{
    image: string
  } | null>(null)

  const handleImageChange = (
    name: string,
    files: File[] | null | undefined,
  ) => {
    if (props.onImagesChange) {
      const file = files ? files[0] : null
      props.onImagesChange(name, file)
    }
  }

  const handleBannerChange = async (files: File[] | null | undefined) => {
    const file = files ? files[0] : null
    if (!file) {
      props.onImagesChange?.('image_banner_url', null)
      return
    }

    try {
      const base64Image = await blobTobase64(file)
      if (
        base64Image &&
        typeof base64Image === 'string' &&
        base64Image.length > 0
      ) {
        // Validate that the base64 data is a valid image before opening cropping tool
        const isValidImage = await validateBase64Image(base64Image)
        if (isValidImage) {
          setCroppingToolOptions({ image: base64Image })
        } else {
          pushUINotification({
            message: 'Invalid image file. Please select a valid image.',
            level: 'error',
          })
        }
      } else {
        pushUINotification({
          message: 'Failed to process image. Please try a different file.',
          level: 'error',
        })
      }
    } catch {
      pushUINotification({
        message: 'Failed to load image. Please try a different file.',
        level: 'error',
      })
    }
  }

  const handleCropApply = async ({ base64Image }: { base64Image: string }) => {
    setCroppingToolOptions(null)
    if (props.onImagesChange) {
      try {
        const croppedFile = await base64ToBlob(base64Image)
        props.onImagesChange('image_banner_url', croppedFile)
      } catch {
        pushUINotification({
          message: 'Failed to process cropped image. Please try again.',
          level: 'error',
        })
      }
    }
  }

  let logo = props.images['image_logo_url']

  if (logo && logo.includes && logo.startsWith('http') && !logo.includes('?')) {
    logo = getProcessedLogo(logo)
  }

  const bannerNode = props.bannerImageNode || (
    <div
      style={{
        ...getBackgroundStyle(props.images['image_banner_url']),
        height: BANNER_HEIGHT,
      }}
    />
  )
  return (
    <div className="library-header">
      <div className="library-header-banner">
        {props.onImagesChange ? (
          <>
            <div className={styles.bannerContainer}>
              <FileSelector
                caption={[
                  'Drag banner image here, or click to add banner image',
                  'Click to change banner image',
                ]}
                helpText="Optimal dimensions 2400 x 800px (3:1 ratio)"
                image={props.images['image_banner_url']}
                type="image"
                onDrop={handleBannerChange}
                dnd
              />
            </div>
            <Flex justify="end" pt="2" pr="3">
              <BannerImageRecommendationsHoverCard>
                <Link size="2" asChild>
                  <span>Image recommendations</span>
                </Link>
              </BannerImageRecommendationsHoverCard>
            </Flex>
          </>
        ) : (
          bannerNode
        )}
      </div>

      {croppingToolOptions && (
        <CroppingTool
          src={croppingToolOptions.image}
          overlayWidth={BANNER_CROP_WIDTH}
          overlayHeight={BANNER_CROP_HEIGHT}
          onClose={() => setCroppingToolOptions(null)}
          onApply={handleCropApply}
        />
      )}

      <div className="library-header-bottom">
        {(props.onImagesChange || logo) && (
          <div className="library-header-logo">
            {props.onImagesChange ? (
              <FileSelector
                caption={[
                  'Drag logo here, or click to add logo',
                  'Click to change logo',
                ]}
                helpText="320 x 320px"
                image={logo}
                height={160}
                type="image"
                rounded
                onDrop={files => handleImageChange('image_logo_url', files)}
                dnd
              />
            ) : (
              <div
                style={{ ...getBackgroundStyle(logo), width: 160, height: 160 }}
              />
            )}
          </div>
        )}
        <div className="library-header-content">
          <Heading size={900}>{props.title}</Heading>
          {props.description && (
            <div className="library-header-description">
              {props.description}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LibraryHeader
